import { ScramjetClient } from "@client/index";
import { Object_keys, Reflect_get, Reflect_ownKeys } from "@/shared/snapshot";

export default function (client: ScramjetClient, self: Self) {
	// Every site's entries live in the real storage as "<host>@<key>".
	const prefix = () => client.url.host + "@";
	const ownKeys = (target: Storage) =>
		Object_keys(target).filter((key) => key.startsWith(prefix()));

	const handler: ProxyHandler<Storage> = {
		get(target, prop) {
			switch (prop) {
				case "getItem":
					return (key: string) => {
						return target.getItem(prefix() + key);
					};

				case "setItem":
					return (key: string, value: string) => {
						return target.setItem(prefix() + key, value);
					};

				case "removeItem":
					return (key: string) => {
						return target.removeItem(prefix() + key);
					};

				case "clear":
					return () => {
						for (const key of ownKeys(target)) {
							target.removeItem(key);
						}
					};

				case "key":
					return (index: number) => {
						const key = ownKeys(target)[index];

						return key === undefined ? null : key.substring(prefix().length);
					};

				case "length":
					return ownKeys(target).length;

				default:
					if (prop in Object.prototype || typeof prop === "symbol") {
						return Reflect_get(target, prop);
					}

					return target.getItem(prefix() + (prop as string));
			}
		},

		set(target, prop, value) {
			target.setItem(prefix() + (prop as string), value);

			return true;
		},

		has(target, prop) {
			return target.getItem(prefix() + (prop as string)) !== null;
		},

		ownKeys(target) {
			return Reflect_ownKeys(target)
				.filter((f) => typeof f === "string" && f.startsWith(prefix()))
				.map((f) =>
					typeof f === "string" ? f.substring(prefix().length) : f
				);
		},

		getOwnPropertyDescriptor(target, property) {
			// TODO: probably not right
			if (target.getItem(prefix() + (property as string)) === null) {
				return undefined;
			}

			return {
				value: target.getItem(prefix() + (property as string)),
				enumerable: true,
				configurable: true,
				writable: true,
			};
		},

		defineProperty(target, property, attributes) {
			target.setItem(prefix() + (property as string), attributes.value);

			return true;
		},
	};

	const localStorageProxy = new Proxy(self.localStorage, handler);
	const sessionStorageProxy = new Proxy(self.sessionStorage, handler);

	delete self.localStorage;
	delete self.sessionStorage;

	self.localStorage = localStorageProxy;
	self.sessionStorage = sessionStorageProxy;

	// Calling the real Storage methods on the proxies (Storage.prototype.setItem.call(
	// localStorage, …), as the Osano cookie banner does) threw "Illegal invocation": hand
	// such calls to the proxy.
	const storageProto = self.Storage?.prototype;
	if (storageProto) {
		for (const method of [
			"getItem",
			"setItem",
			"removeItem",
			"key",
			"clear",
		] as const) {
			const original = storageProto[method];
			if (typeof original !== "function") continue;
			Object.defineProperty(storageProto, method, {
				value: new Proxy(original, {
					apply(fn, thisArg, args) {
						if (thisArg === localStorageProxy || thisArg === sessionStorageProxy) {
							return (thisArg as any)[method](...args);
						}

						return Reflect.apply(fn, thisArg, args);
					},
				}),
				writable: true,
				configurable: true,
				enumerable: true,
			});
		}
	}

	// new StorageEvent("storage", { storageArea: localStorage }): the browser takes only a real
	// Storage there and threw "Failed to convert value to 'Storage'" for the proxy (Wolt's page
	// stayed blank). The real one holds every proxied site's data and must not be handed out,
	// so the event is made without it and carries the proxy as its storageArea instead.
	const isProxy = (area: unknown) =>
		area === localStorageProxy || area === sessionStorageProxy;
	const carry = (event: any, area: unknown) => {
		Object.defineProperty(event, "storageArea", {
			get: () => area,
			configurable: true,
			enumerable: true,
		});

		return event;
	};
	if (self.StorageEvent) {
		client.Proxy("StorageEvent", {
			construct(ctx) {
				const init = ctx.args[1];
				if (init && typeof init === "object" && isProxy(init.storageArea)) {
					const area = init.storageArea;
					ctx.args[1] = { ...init, storageArea: null };
					ctx.return(carry(ctx.call(), area));
				}
			},
		});
		client.Proxy("StorageEvent.prototype.initStorageEvent", {
			apply(ctx) {
				const area = ctx.args[7];
				if (isProxy(area)) {
					ctx.args[7] = null;
					ctx.call();
					carry(ctx.this, area);
					ctx.return(undefined);
				}
			},
		});
	}
}
